import React, { useState, useEffect } from "react";
import { Box, IconButton, Tooltip, Typography, useTheme as useMuiTheme } from "@mui/material";
import { Checklist as ChecklistIcon } from "@mui/icons-material";
import { API_CONFIG, API_ENDPOINTS, STORAGE_KEYS } from "../helpers/constants";
import { getFetchErrorMessage } from "../helpers/errorUtils";
import TaskAssignmentPanel from "./TaskAssignmentPanel";
import { useTheme } from "../contexts/ThemeContext";

// Task interface matching the structure used in ThreadDetailPage
export interface Task {
  id: string;
  task_details: {
    task_name: string;
    status: string;
    assignee: {
      id: string;
      name: string;
      email_id: string;
      avatar_url: string | null;
      username: string;
    };
    created_by?: {
      avatar_url: string;
      name: string;
    };
    priority: string;
    description: string;
    due_date: string;
    created_at: string;
    updated_at: string;
    source: string;
    tags: string[];
  };
  task_history?: Array<{
    action: string;
    details: string;
    timestamp: string;
    user: {
      id: string;
      name: string;
      email_id: string;
      avatar_url: string | null;
      role: string;
    };
  }>;
  context?: {
    workspace: {
      id: string;
      name: string;
      description: string;
    };
    channel: {
      id: string;
      name: string;
      description: string;
      type: string;
      is_private: boolean;
    };
  };
}

interface TasksResponse {
  tasks: Task[];
  total?: number;
  page?: number;
  size?: number;
}

export interface TaskPanelProps {
  threadId?: string;
  channelId?: string;
  selectedMessageId?: string | null;
  messages: any[]; // Messages array to check if tasks can be assigned
  onTaskUpdate?: () => void; // Callback when tasks are updated
  onSelectedMessageIdChange?: (messageId: string | null) => void; // Callback to update selected message ID
  open?: boolean; // External control for panel open state
  onOpenChange?: (open: boolean) => void; // Callback when panel open state changes
}

const TaskPanel: React.FC<TaskPanelProps> = ({
  threadId,
  channelId,
  selectedMessageId,
  messages,
  onTaskUpdate,
  onSelectedMessageIdChange,
  open,
  onOpenChange,
}) => {
  const muiTheme = useMuiTheme();
  const { isDarkMode } = useTheme();

  // State for tasks
  const [tasks, setTasks] = useState<Task[]>([]);
  const [tasksLoading, setTasksLoading] = useState<boolean>(false);
  const [tasksError, setTasksError] = useState<string | null>(null);

  // State for task assignment panel (use external control if provided, otherwise internal state)
  const [isTaskAssignmentPanelOpen, setIsTaskAssignmentPanelOpen] = useState(false);
  
  // Sync internal state with external open prop
  useEffect(() => {
    if (open !== undefined) {
      setIsTaskAssignmentPanelOpen(open);
    }
  }, [open]);

  // Fetch tasks for a specific thread
  const fetchTasks = async (threadId: string) => {
    if (!threadId) return;

    try {
      setTasksLoading(true);
      setTasksError(null);

      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.TASKS.LIST}?thread_id=${threadId}`,
        {
          method: "GET",
          headers: {
            accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: TasksResponse = await response.json();
      setTasks(data.tasks || []);
    } catch (error) {
      setTasksError(
        getFetchErrorMessage(error, "Failed to fetch tasks")
      );
    } finally {
      setTasksLoading(false);
    }
  };

  // Fetch tasks when threadId changes
  useEffect(() => {
    if (threadId) {
      fetchTasks(threadId);
    } else {
      setTasks([]);
    }
  }, [threadId]);

  // Handle task assignment completion
  const handleTaskAssigned = () => {
    if (threadId) {
      fetchTasks(threadId);
    }
    if (onTaskUpdate) {
      onTaskUpdate();
    }
  };

  // Handle opening task assignment panel
  const handleOpenTaskPanel = () => {
    // Ensure we have a selected message ID, fallback to first message if none selected
    if (!selectedMessageId && messages.length > 0 && onSelectedMessageIdChange) {
      onSelectedMessageIdChange(messages[0].id);
    }
    setIsTaskAssignmentPanelOpen(true);
    // Notify parent component of state change
    if (onOpenChange) {
      onOpenChange(true);
    }
  };

  // Handle closing task assignment panel
  const handleCloseTaskPanel = () => {
    setIsTaskAssignmentPanelOpen(false);
    // Notify parent component of state change
    if (onOpenChange) {
      onOpenChange(false);
    }
  };

  return (
    <>
      <Tooltip
        title={
          messages.length === 0
            ? "No messages in thread"
            : tasks.length > 0
            ? tasks[0]?.task_details?.assignee?.name || "Unknown User"
            : "Assign Task"
        }
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: "white",
              color: "black",
              border: "1px solid #ccc",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            },
          },
        }}
      >
        {/* Wrapper lets Tooltip receive events when the button is disabled (disabled buttons do not fire events). */}
        <Box component="span" sx={{ display: "inline-flex" }}>
        <IconButton
          data-testid="task-assignment-button"
          size="small"
          disabled={messages.length === 0}
          onClick={handleOpenTaskPanel}
          sx={{
            color:
              messages.length === 0
                ? muiTheme.palette.text.disabled
                : muiTheme.palette.text.secondary,
            "&:hover": {
              backgroundColor:
                messages.length === 0
                  ? "transparent"
                  : muiTheme.palette.action.hover,
              color:
                messages.length === 0
                  ? muiTheme.palette.text.disabled
                  : muiTheme.palette.text.secondary,
            },
            "&.Mui-disabled": {
              color: muiTheme.palette.text.disabled,
            },
          }}
        >
          {/* Task Status Icon - Shows status of first task if available */}
          {tasks.length > 0 ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                backgroundColor:
                  tasks[0]?.task_details?.status === "Open"
                    ? isDarkMode
                      ? muiTheme.palette.grey[800]
                      : "#f5f5f5"
                    : tasks[0]?.task_details?.status === "In Progress"
                    ? isDarkMode
                      ? muiTheme.palette.primary.dark
                      : "#e3f2fd"
                    : tasks[0]?.task_details?.status === "On Hold"
                    ? isDarkMode
                      ? muiTheme.palette.error.dark
                      : "#ffebee"
                    : tasks[0]?.task_details?.status === "Closed"
                    ? isDarkMode
                      ? muiTheme.palette.secondary.dark
                      : "#f3e5f5"
                    : isDarkMode
                    ? muiTheme.palette.grey[800]
                    : "#f5f5f5",
                borderRadius: 2,
                px: 1.5,
                py: 0.5,
                minWidth: "fit-content",
                border: `1px solid ${
                  tasks[0]?.task_details?.status === "Open"
                    ? isDarkMode
                      ? muiTheme.palette.grey[700]
                      : "#e0e0e0"
                    : tasks[0]?.task_details?.status === "In Progress"
                    ? isDarkMode
                      ? muiTheme.palette.primary.main
                      : "#bbdefb"
                    : tasks[0]?.task_details?.status === "On Hold"
                    ? isDarkMode
                      ? muiTheme.palette.error.main
                      : "#ffcdd2"
                    : tasks[0]?.task_details?.status === "Closed"
                    ? isDarkMode
                      ? muiTheme.palette.secondary.main
                      : "#e1bee7"
                    : isDarkMode
                    ? muiTheme.palette.grey[700]
                    : "#e0e0e0"
                }`,
                cursor: "pointer",
              }}
            >
              {/* Assignee initial */}
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  backgroundColor:
                    tasks[0]?.task_details?.status === "Open"
                      ? "#666"
                      : tasks[0]?.task_details?.status === "In Progress"
                      ? "#1976d2"
                      : tasks[0]?.task_details?.status === "On Hold"
                      ? "#d32f2f"
                      : tasks[0]?.task_details?.status === "Closed"
                      ? "#9c27b0"
                      : "#666",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  fontSize: "10px",
                  fontWeight: "bold",
                  flexShrink: 0,
                }}
              >
                {tasks[0]?.task_details?.assignee?.name?.charAt(0)?.toUpperCase() || "U"}
              </Box>

              {/* Status text */}
              <Typography
                variant="caption"
                sx={{
                  fontSize: "10px",
                  fontWeight: "500",
                  color:
                    tasks[0]?.task_details?.status === "Open"
                      ? isDarkMode
                        ? muiTheme.palette.text.secondary
                        : "#666"
                      : tasks[0]?.task_details?.status === "In Progress"
                      ? isDarkMode
                        ? muiTheme.palette.primary.light
                        : "#1976d2"
                      : tasks[0]?.task_details?.status === "On Hold"
                      ? isDarkMode
                        ? muiTheme.palette.error.light
                        : "#d32f2f"
                      : tasks[0]?.task_details?.status === "Closed"
                      ? isDarkMode
                        ? muiTheme.palette.secondary.light
                        : "#9c27b0"
                      : isDarkMode
                      ? muiTheme.palette.text.secondary
                      : "#666",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  whiteSpace: "nowrap",
                }}
              >
                {tasks[0]?.task_details?.status || "Task"}
              </Typography>

              {/* Checklist Icon */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color:
                    tasks[0]?.task_details?.status === "Open"
                      ? isDarkMode
                        ? muiTheme.palette.text.secondary
                        : "#666"
                      : tasks[0]?.task_details?.status === "In Progress"
                      ? isDarkMode
                        ? muiTheme.palette.primary.light
                        : "#1976d2"
                      : tasks[0]?.task_details?.status === "On Hold"
                      ? isDarkMode
                        ? muiTheme.palette.error.light
                        : "#d32f2f"
                      : tasks[0]?.task_details?.status === "Closed"
                      ? isDarkMode
                        ? muiTheme.palette.secondary.light
                        : "#9c27b0"
                      : isDarkMode
                      ? muiTheme.palette.text.secondary
                      : "#666",
                  ml: 0.5,
                }}
              >
                <ChecklistIcon
                  sx={{
                    fontSize: 16,
                    color:
                      tasks[0]?.task_details?.status === "Open"
                        ? isDarkMode
                          ? muiTheme.palette.text.secondary
                          : "#666"
                        : tasks[0]?.task_details?.status === "In Progress"
                        ? isDarkMode
                          ? muiTheme.palette.primary.light
                          : "#1976d2"
                        : tasks[0]?.task_details?.status === "On Hold"
                        ? isDarkMode
                          ? muiTheme.palette.error.light
                          : "#d32f2f"
                        : tasks[0]?.task_details?.status === "Closed"
                        ? isDarkMode
                          ? muiTheme.palette.secondary.light
                          : "#9c27b0"
                        : isDarkMode
                        ? muiTheme.palette.text.secondary
                        : "#666",
                  }}
                />
              </Box>
            </Box>
          ) : (
            /* Fallback to ChecklistIcon with text when no tasks */
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                backgroundColor: isDarkMode
                  ? muiTheme.palette.grey[800]
                  : "#f5f5f5",
                borderRadius: 2,
                px: 1.5,
                py: 0.5,
                minWidth: "fit-content",
                border: `1px solid ${
                  isDarkMode
                    ? muiTheme.palette.grey[700]
                    : "#e0e0e0"
                }`,
                cursor: "pointer",
              }}
            >
              {/* Default assignee initial placeholder */}
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  backgroundColor: "#666",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 10,
                    fontWeight: "bold",
                    color: "white",
                    lineHeight: 1,
                  }}
                >
                  T
                </Typography>
              </Box>

              {/* Task Status Text */}
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: isDarkMode
                    ? muiTheme.palette.text.secondary
                    : "#666",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  whiteSpace: "nowrap",
                }}
              >
                Task
              </Typography>

              {/* Checklist Icon */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: isDarkMode
                    ? muiTheme.palette.text.secondary
                    : "#666",
                  ml: 0.5,
                }}
              >
                <ChecklistIcon
                  sx={{
                    fontSize: 16,
                    color: isDarkMode
                      ? muiTheme.palette.text.secondary
                      : "#666",
                  }}
                />
              </Box>
            </Box>
          )}
        </IconButton>
        </Box>
      </Tooltip>

      {/* Task Assignment Panel */}
      <TaskAssignmentPanel
        data-testid="task-assignment-panel"
        open={isTaskAssignmentPanelOpen}
        onClose={handleCloseTaskPanel}
        channelId={channelId}
        threadId={threadId}
        messageId={selectedMessageId || undefined}
        tasks={tasks}
        onTaskAssigned={handleTaskAssigned}
        tasksLoading={tasksLoading}
      />
    </>
  );
};

export default TaskPanel;
